import { z } from 'zod';
import { UsageSchema } from './account';

/** Media type of error responses (RFC 9457 Problem Details). */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';

/** Stable, machine-readable error codes. Clients should branch on these, not on messages. */
export const ERROR_CODES = [
  'BAD_REQUEST',
  'VALIDATION_FAILED',
  'NOT_FOUND',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'INVALID_SIGN_IN_CODE',
  'RATE_LIMITED',
  'CREDIT_LIMIT_REACHED',
  'ALREADY_SUBSCRIBED',
  'NOT_SUBSCRIBED',
  'SUBSCRIPTION_CHANGE_REFUSED',
  'API_KEY_LIMIT_REACHED',
  'TONE_NOT_FOUND',
  'TONE_LIMIT_REACHED',
  'INSTRUCTION_NOT_FOUND',
  'INSTRUCTION_LIMIT_REACHED',
  'TOOL_NOT_FOUND',
  'TOOL_LIMIT_REACHED',
  'DRAFT_NOT_FOUND',
  'DRAFT_LIMIT_REACHED',
  'ACCOUNT_NOT_DELETABLE',
  'REWRITE_REFUSED',
  'CONTENT_REJECTED',
  /** An image had no text to read; it can still be described. */
  'NO_TEXT_FOUND',
  'UPSTREAM_ERROR',
  'UPSTREAM_TIMEOUT',
  'SERVICE_UNAVAILABLE',
  'INTERNAL_ERROR',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ProblemSchema = z
  .object({
    type: z.string().describe('A URI reference identifying the problem type.'),
    title: z.string().describe('A short summary of the problem type.'),
    status: z.int().describe('The HTTP status code.'),
    detail: z.string().optional().describe('A human-readable explanation of this occurrence.'),
    code: z.enum(ERROR_CODES).describe('A stable, machine-readable error code.'),
    requestId: z.string().optional().describe('Identifies the request in server logs.'),
    retryAfter: z
      .int()
      .nonnegative()
      .optional()
      .describe('Seconds to wait before retrying. Mirrors the Retry-After header.'),
    errors: z
      .array(z.object({ path: z.string(), message: z.string() }))
      .optional()
      .describe('Field-level validation errors.'),
    usage: UsageSchema.optional().describe('Daily credit usage, sent with CREDIT_LIMIT_REACHED.'),
  })
  .meta({ id: 'Problem' });

export type Problem = z.infer<typeof ProblemSchema>;
